import { FC, FormEvent, useState } from 'react'
import { AssinaturaType, ButtonType, IconPaths } from '_metronic/helpers'
import { Input } from 'app/components/Input/Input'
import { useCertificadoAssinatura } from '../../hooks/useCertificadoAssinatura'
import styles from '../../Prescricao.module.scss'
import { Icone } from '../comum/Icone'
import { MarcaEstado } from '../comum/MarcaEstado'
import { useRotulos } from '../comum/useRotulos'
import { Button, Radio, CustomSelect, IOption } from 'app/components'

type IProps = {
  certificado: ReturnType<typeof useCertificadoAssinatura>
  quantidade: number
  onCancelar: () => void
  /** Certificado pronto (senha validada, se era o caso): pode começar a assinar. */
  onPronto: (tipo: AssinaturaType) => void
}

const ID_SENHA = 'assinatura-senha'

/**
 * Escolha do certificado e liberação para assinar. A1 pede a senha só se o login ainda não
 * está liberado; em nuvem não há senha, e sem credencial autorizada aparece o provedor.
 */
export const EtapaCertificado: FC<IProps> = ({ certificado, quantidade, onCancelar, onPronto }) => {
  const { t } = useRotulos()
  const [senha, setSenha] = useState('')
  const { tipo, estado } = certificado

  const enviar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (estado === 'senha') {
      if (!senha) return document.getElementById(ID_SENHA)?.focus()
      const ok = await certificado.confirmarSenha(senha)
      if (!ok) return document.getElementById(ID_SENHA)?.focus()
    } else if (estado !== 'pronto') {
      return
    }
    certificado.lembrarTipo()
    onPronto(tipo)
  }

  const podeAssinar = estado === 'pronto' || estado === 'senha'
  // Provedor sem URL não abre a autorização: fica fora da lista.
  const opcoesProvedor: IOption<string>[] = certificado.provedores.flatMap((provedor) =>
    provedor.url
      ? [{ value: provedor.url, label: provedor.certificado ?? provedor.provedor ?? provedor.url }]
      : []
  )

  return (
    <form onSubmit={enviar} className='d-flex flex-column gap-5'>
      <p className='m-0'>{t('prescricao.assinatura.explicacao', { quantidade })}</p>

      {certificado.opcoes.length > 1 && (
        <fieldset className='m-0 p-0 border-0 d-flex flex-column gap-2'>
          <legend className='fs-6 fw-bold mb-2'>{t('prescricao.assinatura.certificado')}</legend>
          <Radio
            id='assinatura-certificado'
            isColumn
            value={tipo}
            onChange={certificado.escolherTipo}
            options={certificado.opcoes.map((opcao) => ({
              id: opcao,
              value: opcao,
              descricao: t(`prescricao.assinatura.tipo.${opcao}`),
            }))}
          />
        </fieldset>
      )}

      {estado === 'verificando' && (
        <p className={`m-0 ${styles.textoSecundario}`} role='status'>
          {t('prescricao.assinatura.verificando')}
        </p>
      )}

      {estado === 'pronto' && (
        <p className={`m-0 fw-semibold ${styles.estadoOk} ${styles.comIcone}`} role='status'>
          <Icone icone={IconPaths.Check} />
          {t(
            tipo === AssinaturaType.A1
              ? 'prescricao.assinatura.a1_liberado'
              : 'prescricao.assinatura.a3_liberado'
          )}
        </p>
      )}

      {estado === 'senha' && (
        <div className='d-flex flex-column gap-1'>
          <label htmlFor={ID_SENHA} className='fs-7 fw-semibold'>
            {t('prescricao.assinatura.senha')}
          </label>
          <Input
            id={ID_SENHA}
            type='password'
            autoComplete='current-password'
            value={senha}
            placeholder=''
            isInvalid={certificado.senhaIncorreta}
            errorMessage={t('prescricao.assinatura.senha_incorreta')}
            onChange={(evento) => setSenha(evento.target.value)}
          />
          <span className={`fs-8 ${styles.textoSecundario}`}>
            {t('prescricao.assinatura.senha_dica')}
          </span>
        </div>
      )}

      {(estado === 'provedor' || estado === 'aguardando') && (
        <div className='d-flex flex-column gap-2'>
          <label htmlFor='assinatura-provedor' className='fs-7 fw-semibold'>
            {t('prescricao.assinatura.provedor')}
          </label>
          <CustomSelect
            inputId='assinatura-provedor'
            className={styles.campoEstreito}
            placeholder={t('prescricao.assinatura.provedor_selecione')}
            options={opcoesProvedor}
            value={opcoesProvedor.find((opcao) => opcao.value === certificado.urlProvedor)}
            onChange={(opcao: IOption<string> | null) => certificado.escolherProvedor(opcao?.value)}
          />
          <span className={`fs-8 ${styles.textoSecundario}`}>
            {t('prescricao.assinatura.provedor_dica')}
          </span>
          {certificado.urlProvedor && (
            <>
              <iframe
                title={t('prescricao.assinatura.provedor')}
                src={certificado.urlProvedor}
                className={styles.iframeProvedor}
              />
              <p className={`m-0 ${styles.estadoAndamento} ${styles.comIcone}`} role='status'>
                <MarcaEstado marca='andamento' />
                {t('prescricao.assinatura.aguardando')}
              </p>
            </>
          )}
        </div>
      )}

      {certificado.erro && (
        <p className={`m-0 ${styles.mensagemErro}`} role='alert'>
          {certificado.erro}
        </p>
      )}

      <div className='d-flex justify-content-end gap-2'>
        <Button className='btn-secondary' onClick={onCancelar}>
          {t('common.cancel')}
        </Button>
        <Button
          type={ButtonType.Submit}
          className='btn-primary'
          disabled={!podeAssinar || certificado.enviando}
        >
          {t('prescricao.assinatura.assinar', { quantidade })}
        </Button>
      </div>
    </form>
  )
}
